// NowPlayingBar.jsx — mini persistent audio bar shown above the tab bar on
// every tab EXCEPT the brief detail (which has its own full player).
//
// Behavior:
//   - Renders only when audioPlayerStore has a loaded brief (status 'ready').
//   - Shows title + progress strip, with Play/Pause and Close (X).
//   - Tapping the title jumps the user to the Intelligence tab (the caller
//     passes onJumpToBrief so the app can actually flip tabs).
//   - Close fully stops playback and removes the bar.
//   - The bar is draggable vertically between two slots (top-of-screen and
//     just-above-the-tab-bar) so the user can move it out of the way of
//     anything they're trying to interact with — e.g. the monthly/yearly
//     goal inputs on the Momentum Report, which sit high enough that the
//     bottom-anchored bar was blocking the tap target.
//
// Implementation notes:
//   * We use useSyncExternalStore to subscribe. Because the audio element
//     lives in the store module and not in React, it keeps playing during
//     tab switches and when the app goes to the background — the whole point.
//   * Position is anchored via CSS `top` in both slots (never `bottom`), so
//     transitioning between them is a simple single-property animation that
//     Safari interpolates cleanly. During an active drag we layer a
//     transform:translateY on top, then clear it on release.
//   * The chosen slot is persisted in localStorage so the user's preference
//     survives tab switches, app restarts, and new briefs starting.

import { useSyncExternalStore, useState, useRef } from 'react';
import { Icon } from './Icon.jsx';
import * as audioStore from '../lib/audioPlayerStore.js';

const POS_KEY = 'proxera.nowPlayingPos.v1';
const BAR_HEIGHT = 66;            // approximate — used to compute the bottom slot offset
const DRAG_THRESHOLD = 48;        // px of vertical travel before we commit to a slot switch

function readPos() {
  try {
    const v = localStorage.getItem(POS_KEY);
    return v === 'top' ? 'top' : 'bottom';
  } catch { return 'bottom'; }
}
function writePos(p) {
  try { localStorage.setItem(POS_KEY, p); } catch {}
}

export function NowPlayingBar({ onJumpToBrief, hidden = false }) {
  const s = useSyncExternalStore(audioStore.subscribe, audioStore.getState, audioStore.getState);
  const [pos, setPos] = useState(readPos);
  const [dragY, setDragY] = useState(0);
  const [dragging, setDragging] = useState(false);
  const dragRef = useRef({ startY: 0, pointerId: null, moved: false });

  // Only render when there's an actual loaded brief.
  if (hidden) return null;
  if (!s.brief) return null;
  if (s.status !== 'ready') return null;

  const pct = s.dur > 0 ? Math.min(100, Math.max(0, (s.t / s.dur) * 100)) : 0;

  // Two anchored slots, both expressed as `top` values so we can transition
  // between them smoothly. The bottom slot accounts for the floating tab
  // bar's 18px bottom inset + 68px height + 8px breathing gap = 94px.
  const topSlot    = 'calc(env(safe-area-inset-top) + 8px)';
  const bottomSlot = `calc(100% - ${BAR_HEIGHT}px - env(safe-area-inset-bottom) - 94px)`;
  const anchoredTop = pos === 'top' ? topSlot : bottomSlot;

  // ── Drag handling ────────────────────────────────────────────────
  // We attach handlers to the wrapper. If the pointer lands on a <button>
  // inside the bar (play/pause, title, close), we don't treat it as a drag
  // — those are tap targets and their onClick must fire normally. Once
  // movement exceeds ~8px we flip into drag mode and start applying a
  // transform. On release, if the user dragged far enough in the direction
  // opposite their current slot, we switch slots and persist.
  const onPointerDown = (e) => {
    if (e.target?.closest && e.target.closest('button')) return;
    dragRef.current = { startY: e.clientY, pointerId: e.pointerId, moved: false };
    setDragging(true);
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch {}
  };
  const onPointerMove = (e) => {
    if (!dragging) return;
    if (e.pointerId !== dragRef.current.pointerId) return;
    const delta = e.clientY - dragRef.current.startY;
    if (Math.abs(delta) > 4) dragRef.current.moved = true;
    setDragY(delta);
  };
  const finishDrag = (e) => {
    if (!dragging) return;
    const delta = (e && e.clientY != null)
      ? e.clientY - dragRef.current.startY
      : dragY;
    let nextPos = pos;
    if (pos === 'bottom' && delta < -DRAG_THRESHOLD) nextPos = 'top';
    else if (pos === 'top' && delta > DRAG_THRESHOLD) nextPos = 'bottom';
    if (nextPos !== pos) {
      setPos(nextPos);
      writePos(nextPos);
    }
    setDragging(false);
    setDragY(0);
    try { e?.currentTarget?.releasePointerCapture?.(dragRef.current.pointerId); } catch {}
  };

  // Tap on the grabber (without dragging) toggles between slots. Gives users
  // who don't discover the drag affordance a one-tap fallback.
  const onGrabberClick = () => {
    if (dragRef.current.moved) return;     // drag, not a click
    const next = pos === 'top' ? 'bottom' : 'top';
    setPos(next);
    writePos(next);
  };

  return (
    <div
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={finishDrag}
      onPointerCancel={finishDrag}
      style={{
        position: 'absolute',
        left: 10, right: 10,
        top: anchoredTop,
        transform: dragging ? `translateY(${dragY}px)` : 'translateY(0)',
        // Hold still during an active drag so motion feels 1:1 with the
        // finger; only apply the spring when we snap back or commit.
        transition: dragging
          ? 'none'
          : 'top 360ms cubic-bezier(.22,1,.36,1), transform 280ms cubic-bezier(.22,1,.36,1)',
        zIndex: 60,
        borderRadius: 16,
        background: 'radial-gradient(circle at 100% 0%, rgba(45,156,255,0.16), transparent 60%), rgba(19,21,25,0.95)',
        backdropFilter: 'blur(30px) saturate(180%)',
        WebkitBackdropFilter: 'blur(30px) saturate(180%)',
        border: '1px solid var(--stroke-1)',
        boxShadow: dragging
          ? '0 16px 40px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.08)'
          : '0 -10px 30px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.06)',
        padding: '14px 10px 11px 12px',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        touchAction: 'none',   // keep the browser from hijacking our vertical drag as page scroll
        cursor: dragging ? 'grabbing' : 'grab',
      }}
      data-silent="true" // the pointerdown click-sfx handler should ignore this wrapper
    >
      {/* Grabber pill — visual affordance for drag + quick-toggle tap. */}
      <div
        role="button"
        aria-label={pos === 'top' ? 'Move player to bottom' : 'Move player to top'}
        onClick={onGrabberClick}
        style={{
          position: 'absolute',
          top: 5, left: '50%',
          transform: 'translateX(-50%)',
          width: 38, height: 4,
          borderRadius: 2,
          background: dragging ? 'rgba(255,255,255,0.35)' : 'rgba(255,255,255,0.18)',
          transition: 'background 180ms ease',
          cursor: 'grab',
        }}
      />

      {/* Play / pause */}
      <button
        onClick={() => audioStore.toggle()}
        aria-label={s.playing ? 'Pause' : 'Play'}
        style={{
          width: 36, height: 36, borderRadius: 999,
          background: 'var(--grad-cta)',
          border: 'none',
          display: 'grid', placeItems: 'center',
          boxShadow: '0 0 14px rgba(45,156,255,0.45), inset 0 1px 0 rgba(255,255,255,0.3)',
          cursor: 'pointer',
          flexShrink: 0,
        }}
      >
        <Icon name={s.playing ? 'pause' : 'play'} size={14} color="#fff"/>
      </button>

      {/* Title + progress — clickable to jump to the brief */}
      <button
        onClick={() => {
          // If the user just finished a drag, swallow this click so we don't
          // hijack them to the brief when they meant to move the bar.
          if (dragRef.current.moved) { dragRef.current.moved = false; return; }
          if (typeof onJumpToBrief === 'function' && s.brief?.id) {
            onJumpToBrief(s.brief.id);
          }
        }}
        style={{
          flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column',
          background: 'transparent', border: 'none', padding: 0,
          textAlign: 'left', cursor: 'pointer',
        }}
      >
        <div style={{
          fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 11,
          color: 'var(--accent)', letterSpacing: '0.16em', textTransform: 'uppercase',
        }}>
          Now playing
        </div>
        <div style={{
          fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 12.5,
          color: 'var(--fg-1)', whiteSpace: 'nowrap', overflow: 'hidden',
          textOverflow: 'ellipsis', marginTop: 1,
        }}>
          {s.brief?.title || 'The Briefing'}
        </div>
        <div style={{
          height: 2, borderRadius: 999, background: 'rgba(255,255,255,0.08)',
          marginTop: 5, overflow: 'hidden',
        }}>
          <div style={{ height: '100%', width: `${pct}%`, background: 'var(--grad-cta)' }}/>
        </div>
      </button>

      {/* Close — fully stops audio */}
      <button
        onClick={() => audioStore.stop()}
        aria-label="Stop playback"
        title="Stop"
        className="btn-icon"
        style={{ flexShrink: 0 }}
      >
        <Icon name="x" size={14}/>
      </button>
    </div>
  );
}
